import { RSAA } from 'redux-api-middleware';
import uuid from 'node-uuid';
import { uniqBy, get, flatten, pickBy, find, has } from 'lodash';
import endpoints, { resolveEndpoint } from '../../utils/endpoints';
import { getBankId } from '../session/selectors';
import { SET_PAYEES } from '../payees/actions';

export const SET_ACCOUNTS = 'SET_ACCOUNTS';

export const GET_ACCOUNTS_BY_CAPABILITY_REQUEST = 'GET_ACCOUNTS_BY_CAPABILITY_REQUEST';
export const GET_ACCOUNTS_BY_CAPABILITY_SUCCESS = 'GET_ACCOUNTS_BY_CAPABILITY_SUCCESS';
export const GET_ACCOUNTS_BY_CAPABILITY_FAILURE = 'GET_ACCOUNTS_BY_CAPABILITY_FAILURE';

export const UPDATE_ACCOUNTS = 'UPDATE_ACCOUNTS';

export const setAccounts = (accounts) => ({
	type: SET_ACCOUNTS,
	accounts,
});

// Below code copied from payments-lib

export const updateAccounts = (accounts) => ({
	type: UPDATE_ACCOUNTS,
	accounts,
	meta: {
		allowedDuringApiCall: true,
	},
});

export const setPayees = (payees) => ({
	type: SET_PAYEES,
	payees,
});

export const GET_ALL_MISSING_DETAILS_REQUEST = 'GET_ALL_MISSING_DETAILS_REQUEST';
export const GET_ALL_MISSING_DETAILS_SUCCESS = 'GET_ALL_MISSING_DETAILS_SUCCESS';
export const GET_ALL_MISSING_DETAILS_FAILURE = 'GET_ALL_MISSING_DETAILS_FAILURE';

export const getAccountsByCapability = (capability) => {
	const { x_bpi_version, method } = endpoints.getLcvAccounts;
	const endpoint = endpoints.getLcvAccounts.resolve({
		paymentType: encodeURIComponent(capability),
	});
	return {
		[RSAA]: {
			endpoint,
			method,
			types: [
				GET_ACCOUNTS_BY_CAPABILITY_REQUEST,
				GET_ACCOUNTS_BY_CAPABILITY_SUCCESS,
				GET_ACCOUNTS_BY_CAPABILITY_FAILURE,
			],
			headers: { 'x-bpi-version': x_bpi_version },
		},
	};
};

const getUniqAndFlattenArray = (list) => uniqBy(flatten(list), 'uri');
const getAccountById = (state, id) => state.accounts.find((account) => account.id === id);
const getAccountBySortCodeAccountNumber = (state, sortCode = '', accountNumber = '') =>
	find(state.accounts, { sortCode, accountNumber });

const getBeneficiaryByIdForTemplates = (state, id) => {
	const payee = state.payees.find((payee) => payee.id === id);
	if (payee) {
		payee.isBiller = has(payee, 'biller_id');
	}
	return payee;
};

const getPayeeBySortCodeAccountNumber = (state, sortCode = '', accountNumber = '') =>
	find(
		state.payees,
		(payee) =>
			get(payee, 'to_account.sort_code') === sortCode && get(payee, 'to_account.account_number') === accountNumber
	);

const getMultiPartRequest = (endpoint, urlParams, bankId) => {
	const uri = endpoint.resolve(urlParams).replace('paas-base-url:', '');
	const { method, x_bpi_version } = endpoint;

	return {
		id: uuid.v1(),
		method,
		uri: resolveEndpoint(uri, bankId),
		x_bpi_version,
	};
};

export const getAccountsAndBensMultiPartRequest = (requestBody) => {
	if (!requestBody.length) {
		return null;
	}
	const endpoint = endpoints.multipart.uri;
	const body = JSON.stringify({
		batch_list: requestBody,
	});
	return {
		[RSAA]: {
			endpoint,
			method: 'POST',
			types: [GET_ALL_MISSING_DETAILS_REQUEST, GET_ALL_MISSING_DETAILS_SUCCESS, GET_ALL_MISSING_DETAILS_FAILURE],
			body,
			headers: { 'x-bpi-version': endpoints.multipart.x_bpi_version },
		},
	};
};

const checkIfFromAccountAvailableInStore = ({ getState, from_account = {} }) => {
	const state = getState();
	const bankId = getBankId(state);
	const fromId = get(from_account, 'id', '') || get(from_account, 'account_uuid', '');
	const { sort_code: sortCode = '', account_number: accountNumber = '' } = from_account;
	if (fromId && !getAccountById(state, fromId)) {
		return getMultiPartRequest(endpoints.getAccountDetails, fromId, bankId);
	}
	if (from_account && !getAccountBySortCodeAccountNumber(state, sortCode, accountNumber)) {
		return getMultiPartRequest(endpoints.getLcvAccounts, { accountNumber, sortCode }, bankId);
	}
	return null;
};

const checkIfToSelectionAvailableInStore = ({ getState, to_account = {} }) => {
	const state = getState();
	const bankId = getBankId(state);
	const toId = get(to_account, 'id', '') || get(to_account, 'account_uuid', '');
	const { sort_code: sortCode = '', account_number: accountNumber = '', iban = '', name = '' } = to_account;
	if (toId && !getBeneficiaryByIdForTemplates(state, toId)) {
		return getMultiPartRequest(endpoints.getBeneficiaryById, toId, bankId);
	}
	if (to_account && !getPayeeBySortCodeAccountNumber(state, sortCode, accountNumber || iban)) {
		const toSelectionDetails = pickBy({
			accountNumber: accountNumber || iban,
			sortCode,
			name,
		});
		return getMultiPartRequest(endpoints.getLcvPayees, toSelectionDetails, bankId);
	}
	return null;
};

const getMultipartRequestBodyForPayments = ({ from_account, to_account }, getState) => {
	const requestBody = [];
	requestBody.push(checkIfFromAccountAvailableInStore({ getState, from_account }));
	requestBody.push(checkIfToSelectionAvailableInStore({ getState, to_account }));
	return requestBody.filter((request) => request);
};

export const handleAccountsAndBensMultiPartSuccess = (response) => (dispatch) => {
	if (response.type === GET_ALL_MISSING_DETAILS_SUCCESS) {
		const apiResponses = get(response, 'payload.apiResponses', []);
		if (apiResponses.some((apiResponse) => apiResponse.code !== 200)) {
			return dispatch({
				type: GET_ALL_MISSING_DETAILS_FAILURE,
			});
		}
		const { accounts, beneficiaries } = apiResponses.reduce(
			(results, { body }) => {
				const { accounts = [], beneficiaries = [] } = body;
				if (accounts.length) {
					return {
						...results,
						accounts: [...results.accounts, ...accounts],
					};
				}
				if (beneficiaries.length) {
					return {
						...results,
						beneficiaries: [...results.beneficiaries, ...beneficiaries],
					};
				}
				return results;
			},
			{ accounts: [], beneficiaries: [] }
		);
		if (accounts.length) {
			dispatch(updateAccounts(accounts));
		}
		if (beneficiaries.length) {
			dispatch(setPayees(beneficiaries));
		}
	}
	return null;
};

export const getMissingAccountsForPaymentLibAuth = (paymentList) => (dispatch, getState) => {
	const requestBody = getUniqAndFlattenArray(
		paymentList.map((payment) => getMultipartRequestBodyForPayments(payment, getState))
	);
	return dispatch(getAccountsAndBensMultiPartRequest(requestBody)).then((response) => {
		dispatch(handleAccountsAndBensMultiPartSuccess(response));
		return response;
	});
};
